import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Scale, Mail } from 'lucide-react';
import { tokenStorage } from '../../lib/tokenStorage';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = tokenStorage.getToken();
  return { Authorization: `Bearer ${token}` };
};

/**
 * DefaultStatusWorkflowDialog — fires AFTER the backend status flip to
 * "Default" succeeds (via either the Mark-As-Default button or the Edit
 * Deal form's status dropdown). The status change itself is already
 * persisted; this dialog is purely a follow-up workflow:
 *
 *   Step 1: "Was this deal sent to a lawyer?"  Yes / No / X
 *   Step 2 (if Yes): Pick a lawyer + confirm contingency rate.
 *                    POSTs to /api/default-recovery/{id}/recovery-mode
 *                    with mode=via_lawyer, lawyer_contact_id, contingency_rate.
 *   Step 3 (after Step 2 succeeds): "Send email package?"  Yes / No.
 *                    Yes → POST /api/default-recovery/{id}/send-default-package
 *                    with law_firm_email = selected lawyer's primary email.
 *                    Skipped entirely if the default_package email template
 *                    is disabled in Admin Settings.
 *
 * Dismissing the dialog at ANY step is a no-op (no rollback, no error).
 * The deal stays in Default with whatever state has been persisted so far.
 */
const DefaultStatusWorkflowDialog = ({ open, onOpenChange, deal, onComplete }) => {
  const dealId = deal?.id;

  // Workflow step machine. 'q-sent-to-lawyer' → 'pick-lawyer' → 'q-send-email'.
  // 'closed' state is unused because we drive the dialog's lifecycle via
  // the `open` prop from the parent.
  const [step, setStep] = useState('q-sent-to-lawyer');

  // Lawyer list + selection state for Step 2
  const [lawyers, setLawyers] = useState([]);
  const [lawyersLoaded, setLawyersLoaded] = useState(false);
  const [selectedLawyerId, setSelectedLawyerId] = useState('');
  const [contingencyRate, setContingencyRate] = useState('25');

  // Email-template-toggle state for Step 3 gating
  const [emailTemplateActive, setEmailTemplateActive] = useState(null); // null = unknown

  // Submit state — single saving flag across both API calls per step
  const [submitting, setSubmitting] = useState(false);

  // Reset when dialog re-opens for a new deal
  useEffect(() => {
    if (open) {
      setStep('q-sent-to-lawyer');
      setSelectedLawyerId('');
      setContingencyRate('25');
      setSubmitting(false);
      // Pre-fetch the email template toggle so Step 3 knows in advance
      axios.get(`${API}/api/admin/settings/email-templates/default_package`, {
        headers: getAuthHeaders(),
      })
        .then((r) => setEmailTemplateActive(r.data?.is_active !== false))
        .catch(() => setEmailTemplateActive(false)); // 404 or error → treat as disabled
    }
  }, [open]);

  // Lazy-load lawyers when entering Step 2
  const fetchLawyersIfNeeded = async () => {
    if (lawyersLoaded) return;
    try {
      const r = await axios.get(`${API}/api/contacts?contact_type=lawyer&limit=200`, {
        headers: getAuthHeaders(),
      });
      const list = (r.data?.items || r.data || []).filter((c) => c.is_lawyer);
      setLawyers(list);
      setLawyersLoaded(true);

      // Pre-select default-for-lawyer contact if one exists
      const defaultLawyer = list.find((c) => (c.is_default_for || []).includes('lawyer') || c.is_default_lawyer);
      if (defaultLawyer) {
        setSelectedLawyerId(defaultLawyer.id);
        const r0 = defaultLawyer.default_contingency_rate;
        if (r0 !== null && r0 !== undefined && r0 !== '') {
          setContingencyRate(String(r0));
        }
      }
    } catch (err) {
      toast.error('Failed to load lawyer contacts');
    }
  };

  // When the user picks a different lawyer, pull that lawyer's
  // default_contingency_rate. Fall back to 25% only if unset.
  const handleLawyerChange = (lawyerId) => {
    setSelectedLawyerId(lawyerId);
    const lawyer = lawyers.find((l) => l.id === lawyerId);
    const r = lawyer?.default_contingency_rate;
    if (r !== null && r !== undefined && r !== '') {
      setContingencyRate(String(r));
    } else {
      setContingencyRate('25');
    }
  };

  // ── Step 1 handlers ────────────────────────────────────────────────
  const handleSentToLawyerNo = () => {
    onOpenChange(false);
    if (onComplete) onComplete();
  };

  const handleSentToLawyerYes = async () => {
    setStep('pick-lawyer');
    await fetchLawyersIfNeeded();
  };

  // ── Step 2 handlers ────────────────────────────────────────────────
  const handleConfirmLawyer = async () => {
    if (!selectedLawyerId) {
      toast.error('Select a lawyer');
      return;
    }
    const rate = parseFloat(contingencyRate);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('Enter a valid contingency rate (0–100)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/default-recovery/${dealId}/recovery-mode`,
        {
          mode: 'via_lawyer',
          lawyer_contact_id: selectedLawyerId,
          contingency_rate: rate,
        },
        { headers: getAuthHeaders() },
      );
      toast.success('Lawyer assigned');
      // Move to Step 3 — unless template is disabled, then close.
      if (emailTemplateActive) {
        setStep('q-send-email');
      } else {
        // Inform once and close.
        toast.message('Email package template is disabled in Admin Settings — skipping email step.');
        onOpenChange(false);
        if (onComplete) onComplete();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to assign lawyer');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 3 handlers ────────────────────────────────────────────────
  const handleSendEmailNo = () => {
    onOpenChange(false);
    if (onComplete) onComplete();
  };

  const handleSendEmailYes = async () => {
    setSubmitting(true);
    try {
      const lawyer = lawyers.find((l) => l.id === selectedLawyerId);
      const primaryEmail = lawyer?.email
        || lawyer?.emails?.[0]?.address
        || lawyer?.emails?.[0];
      await axios.post(
        `${API}/api/default-recovery/${dealId}/send-default-package`,
        primaryEmail ? { law_firm_email: primaryEmail } : {},
        { headers: getAuthHeaders() },
      );
      toast.success('Default package email sent');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send email package');
    } finally {
      setSubmitting(false);
      onOpenChange(false);
      if (onComplete) onComplete();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="default-status-workflow-dialog" className="sm:max-w-md">
        {step === 'q-sent-to-lawyer' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="h-4 w-4" /> Deal marked as Default
              </DialogTitle>
              <DialogDescription>
                Was this deal sent to a lawyer?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={handleSentToLawyerNo}
                data-testid="workflow-sent-to-lawyer-no-btn"
              >
                No
              </Button>
              <Button
                onClick={handleSentToLawyerYes}
                data-testid="workflow-sent-to-lawyer-yes-btn"
              >
                Yes
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'pick-lawyer' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="h-4 w-4" /> Select lawyer
              </DialogTitle>
              <DialogDescription>
                Choose the lawyer assigned to this default recovery.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="workflow-lawyer-select">Lawyer</Label>
                <Select
                  value={selectedLawyerId || ''}
                  onValueChange={handleLawyerChange}
                  disabled={submitting}
                >
                  <SelectTrigger id="workflow-lawyer-select" data-testid="workflow-lawyer-select">
                    <SelectValue placeholder={lawyers.length ? 'Select a lawyer' : 'No lawyer contacts found'} />
                  </SelectTrigger>
                  <SelectContent>
                    {lawyers.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`workflow-lawyer-option-${c.id}`}>
                        {c.company_name}
                        {((c.is_default_for || []).includes('lawyer') || c.is_default_lawyer) ? ' (default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="workflow-contingency-rate">Contingency rate (%)</Label>
                <Input
                  id="workflow-contingency-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={contingencyRate}
                  onChange={(e) => setContingencyRate(e.target.value)}
                  disabled={submitting}
                  data-testid="workflow-contingency-rate-input"
                />
                <p className="text-xs text-muted-foreground" data-testid="workflow-contingency-source">
                  {(() => {
                    const lawyer = lawyers.find((l) => l.id === selectedLawyerId);
                    const r = lawyer?.default_contingency_rate;
                    if (r !== null && r !== undefined && r !== '') {
                      return `Contingency: ${r}% (from contact — editable)`;
                    }
                    return 'Contingency: 25% (default — editable)';
                  })()}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={handleSentToLawyerNo}
                disabled={submitting}
                data-testid="workflow-pick-lawyer-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLawyer}
                disabled={submitting || !selectedLawyerId}
                data-testid="workflow-pick-lawyer-confirm-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'q-send-email' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-4 w-4" /> Send email package?
              </DialogTitle>
              <DialogDescription>
                Send the default package (transaction history + summary) to the assigned lawyer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={handleSendEmailNo}
                disabled={submitting}
                data-testid="workflow-send-email-no-btn"
              >
                No
              </Button>
              <Button
                onClick={handleSendEmailYes}
                disabled={submitting}
                data-testid="workflow-send-email-yes-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Yes, send
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DefaultStatusWorkflowDialog;
